import { useState, useEffect } from "react";
import { Plus, Edit2, Trash2, Building2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { useAdminAuth } from "../../context/AdminAuthContext";
import PropertyAssignmentModal from "../../components/PropertyAssignmentModal";

const INITIAL = {
  first_name: "", last_name: "", email: "", company: "", phone: "",
  country: "", language: "en", notes: "", status: "active",
  welcome_title_en: "", welcome_title_it: "",
  welcome_message_en: "", welcome_message_it: "",
  investor_type: "", preferred_locations: [], preferred_asset_classes: [],
};
const STATUSES = ["active", "inactive"];
const LANGUAGES = [{ value: "en", label: "English" }, { value: "it", label: "Italiano" }];
const INVESTOR_TYPES = [
  { value: "", label: "Not specified" },
  { value: "Family Office", label: "Family Office" },
  { value: "Private Individual", label: "Private Individual" },
  { value: "HNWI", label: "HNWI" },
  { value: "Institutional", label: "Institutional Investor" },
  { value: "Corporate", label: "Corporate" },
  { value: "Fund", label: "Investment Fund" },
];
const FORM_TABS = ["Profile", "Welcome", "Investor"];

export default function UsersPage() {
  const { adminAxios } = useAdminAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(INITIAL);
  const [formTab, setFormTab] = useState("Profile");
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [assigningUser, setAssigningUser] = useState(null);

  const load = () =>
    adminAxios.get("/admin/users").then(({ data }) => setUsers(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(INITIAL);
    setFormTab("Profile");
    setShowForm(true);
  };

  const openEdit = u => {
    setEditing(u);
    setForm({
      first_name: u.first_name || "",
      last_name: u.last_name || "",
      email: u.email || "",
      company: u.company || "",
      phone: u.phone || "",
      country: u.country || "",
      language: u.language || "en",
      notes: u.notes || "",
      status: u.status || "active",
      welcome_title_en: u.welcome_title_en || "",
      welcome_title_it: u.welcome_title_it || "",
      welcome_message_en: u.welcome_message_en || "",
      welcome_message_it: u.welcome_message_it || "",
      investor_type: u.investor_type || "",
      preferred_locations: u.preferred_locations || [],
      preferred_asset_classes: u.preferred_asset_classes || [],
    });
    setFormTab("Profile");
    setShowForm(true);
  };

  const save = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await adminAxios.put(`/admin/users/${editing.id}`, form);
      else await adminAxios.post("/admin/users", form);
      setShowForm(false);
      load();
    } catch (err) { console.error("Save user error:", err); }
    finally { setSaving(false); }
  };

  const del = async id => {
    if (!window.confirm("Delete this user and their access code?")) return;
    await adminAxios.delete(`/admin/users/${id}`);
    load();
  };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const filtered = users.filter(u =>
    `${u.first_name} ${u.last_name} ${u.email} ${u.company}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <div className="p-8 min-h-screen bg-zinc-50">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-zinc-900 tracking-tight">Users</h1>
            <p className="text-sm text-zinc-500 mt-0.5">{users.length} total investors</p>
          </div>
          <button
            onClick={openCreate}
            data-testid="add-user-btn"
            className="flex items-center gap-2 bg-[#C8102E] hover:bg-[#A50D25] text-white text-xs tracking-wider uppercase px-5 py-2.5 transition-colors"
          >
            <Plus size={14} strokeWidth={2} /> Add User
          </button>
        </div>

        <div className="mb-4">
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search users..."
            data-testid="user-search"
            className="border border-zinc-200 px-4 py-2 text-sm w-64 focus:outline-none focus:border-zinc-400 bg-white"
          />
        </div>

        <div className="bg-white border border-zinc-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-100">
                {["Name", "Company", "Email", "Language", "Status", "Code", "Last Login", "Actions"].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {renderUserRows(loading, filtered, openEdit, setAssigningUser, del)}
            </tbody>
          </table>
        </div>

        {/* ── User Form Dialog ── */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto p-0">
            <div className="px-7 pt-7 pb-0">
              <DialogHeader>
                <DialogTitle className="text-lg font-semibold text-zinc-900">
                  {editing ? "Edit Investor" : "Add Investor"}
                </DialogTitle>
                <DialogDescription className="text-xs text-zinc-400 mt-0.5">
                  Manage investor profile, welcome message, and access preferences.
                </DialogDescription>
              </DialogHeader>

              {/* Form Tabs */}
              <div className="flex gap-0 mt-5 border-b border-zinc-100">
                {FORM_TABS.map(tab => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setFormTab(tab)}
                    className={`text-xs tracking-wider uppercase px-4 py-2.5 -mb-px border-b-2 transition-colors ${
                      formTab === tab
                        ? "border-[#C8102E] text-[#C8102E] font-medium"
                        : "border-transparent text-zinc-400 hover:text-zinc-700"
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            <form onSubmit={save} className="px-7 py-5 space-y-4">
              {/* ── Tab: Profile ── */}
              {formTab === "Profile" && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <FieldInput label="First Name *" value={form.first_name} onChange={v => setField("first_name", v)} required />
                    <FieldInput label="Last Name *" value={form.last_name} onChange={v => setField("last_name", v)} required />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <FieldInput label="Email" value={form.email} onChange={v => setField("email", v)} type="email" />
                    <FieldInput label="Phone" value={form.phone} onChange={v => setField("phone", v)} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <FieldInput label="Company" value={form.company} onChange={v => setField("company", v)} />
                    <FieldInput label="Country" value={form.country} onChange={v => setField("country", v)} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <FieldSelect label="Language" value={form.language} onChange={v => setField("language", v)} options={LANGUAGES} />
                    <FieldSelect label="Status" value={form.status} onChange={v => setField("status", v)} options={STATUSES.map(s => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-zinc-600 mb-1">Notes (internal)</label>
                    <textarea
                      value={form.notes}
                      onChange={e => setField("notes", e.target.value)}
                      rows={2}
                      placeholder="Internal notes about this investor..."
                      className="w-full border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:border-zinc-400 resize-none"
                    />
                  </div>
                </>
              )}

              {/* ── Tab: Welcome Message ── */}
              {formTab === "Welcome" && (
                <>
                  <p className="text-xs text-zinc-400 leading-relaxed">
                    Personalised welcome displayed on the investor dashboard. Shown in the investor's selected language with automatic fallback.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-zinc-600 mb-1">Welcome Title (EN)</label>
                      <input
                        type="text"
                        value={form.welcome_title_en}
                        onChange={e => setField("welcome_title_en", e.target.value)}
                        placeholder="Welcome, Marco."
                        className="w-full border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:border-zinc-400"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-zinc-600 mb-1">Welcome Title (IT)</label>
                      <input
                        type="text"
                        value={form.welcome_title_it}
                        onChange={e => setField("welcome_title_it", e.target.value)}
                        placeholder="Benvenuto, Marco."
                        className="w-full border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:border-zinc-400"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-zinc-600 mb-1">Welcome Message (EN)</label>
                    <textarea
                      value={form.welcome_message_en}
                      onChange={e => setField("welcome_message_en", e.target.value)}
                      rows={3}
                      placeholder="A curated selection of opportunities has been prepared exclusively for you."
                      className="w-full border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:border-zinc-400 resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-zinc-600 mb-1">Welcome Message (IT)</label>
                    <textarea
                      value={form.welcome_message_it}
                      onChange={e => setField("welcome_message_it", e.target.value)}
                      rows={3}
                      placeholder="Abbiamo selezionato per Lei una raccolta di opportunità dedicate."
                      className="w-full border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:border-zinc-400 resize-none"
                    />
                  </div>
                  <div className="bg-zinc-50 border border-zinc-100 p-3 rounded">
                    <p className="text-[10px] text-zinc-400 leading-relaxed">
                      <strong className="text-zinc-600">Fallback (EN):</strong> "Welcome. A curated selection of opportunities has been prepared exclusively for you."<br />
                      <strong className="text-zinc-600">Fallback (IT):</strong> "Benvenuto. Abbiamo selezionato per Lei una raccolta di opportunità dedicate."
                    </p>
                  </div>
                </>
              )}

              {/* ── Tab: Investor Profile ── */}
              {formTab === "Investor" && (
                <>
                  <p className="text-xs text-zinc-400 leading-relaxed">
                    Internal CMS fields only. Used to profile the investor and guide future property suggestions.
                  </p>
                  <FieldSelect
                    label="Investor Type"
                    value={form.investor_type}
                    onChange={v => setField("investor_type", v)}
                    options={INVESTOR_TYPES}
                  />
                  <div>
                    <label className="block text-xs font-medium text-zinc-600 mb-1">Preferred Locations</label>
                    <input
                      type="text"
                      value={(form.preferred_locations || []).join(", ")}
                      onChange={e => setField("preferred_locations", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
                      placeholder="Lake Como, Venice, Tuscany, Sardinia..."
                      className="w-full border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:border-zinc-400"
                    />
                    <p className="text-[10px] text-zinc-400 mt-1">Separate multiple locations with commas</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-zinc-600 mb-1">Preferred Asset Classes</label>
                    <input
                      type="text"
                      value={(form.preferred_asset_classes || []).join(", ")}
                      onChange={e => setField("preferred_asset_classes", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
                      placeholder="Prime Residential, Historic Estates, Hospitality..."
                      className="w-full border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:border-zinc-400"
                    />
                    <p className="text-[10px] text-zinc-400 mt-1">Separate multiple asset classes with commas</p>
                  </div>
                </>
              )}

              {/* ── Actions ── */}
              <div className="flex gap-3 pt-3 border-t border-zinc-100">
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="save-user-btn"
                  className="bg-[#C8102E] hover:bg-[#A50D25] text-white text-xs tracking-wider uppercase px-6 py-2.5 transition-colors disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="border border-zinc-200 text-zinc-600 text-xs tracking-wider uppercase px-6 py-2.5 hover:bg-zinc-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* ── Property Assignment Modal (full-screen portal) ── */}
      {assigningUser && (
        <PropertyAssignmentModal
          user={assigningUser}
          onClose={() => setAssigningUser(null)}
          onSaved={load}
        />
      )}
    </>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────

function renderUserRows(loading, users, openEdit, setAssigning, del) {
  if (loading) {
    return <tr><td colSpan={8} className="px-4 py-6 text-zinc-400 text-sm">Loading...</td></tr>;
  }
  if (users.length === 0) {
    return <tr><td colSpan={8} className="px-4 py-8 text-zinc-400 text-sm text-center">No users found.</td></tr>;
  }
  return users.map(u => (
    <tr key={u.id} className="border-b border-zinc-50 hover:bg-zinc-50/80 transition-colors">
      <td className="px-4 py-3 font-medium text-zinc-900 whitespace-nowrap">{u.first_name} {u.last_name}</td>
      <td className="px-4 py-3 text-zinc-500 text-xs">{u.company || "—"}</td>
      <td className="px-4 py-3 text-zinc-500 text-xs">{u.email || "—"}</td>
      <td className="px-4 py-3"><span className="text-xs text-zinc-400 uppercase">{u.language}</span></td>
      <td className="px-4 py-3"><StatusBadge status={u.status} /></td>
      <td className="px-4 py-3 font-mono text-xs text-zinc-400">{u.access_code || "—"}</td>
      <td className="px-4 py-3 text-zinc-400 text-xs whitespace-nowrap">
        {u.last_login ? new Date(u.last_login).toLocaleDateString() : "—"}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          <ActionBtn onClick={() => openEdit(u)} testId={`edit-user-${u.id}`} title="Edit profile"
            icon={<Edit2 size={12} strokeWidth={1.5} />} label="Edit" />
          <ActionBtn onClick={() => setAssigning(u)} testId={`properties-user-${u.id}`} title="Manage property assignments"
            icon={<Building2 size={12} strokeWidth={1.5} />} label="Properties" accent />
          <button onClick={() => del(u.id)} data-testid={`delete-user-${u.id}`}
            className="text-zinc-300 hover:text-[#C8102E] transition-colors p-1" title="Delete user">
            <Trash2 size={12} strokeWidth={1.5} />
          </button>
        </div>
      </td>
    </tr>
  ));
}

function StatusBadge({ status }) {
  const map = { active: "bg-green-50 text-green-700", inactive: "bg-zinc-100 text-zinc-500" };
  return (
    <span className={`text-xs px-2 py-0.5 font-medium ${map[status] || "bg-zinc-100 text-zinc-500"}`}>
      {status}
    </span>
  );
}

function ActionBtn({ onClick, testId, title, icon, label, accent }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      title={title}
      className={`flex items-center gap-1 text-[10px] tracking-wider uppercase px-2 py-1 border transition-colors ${
        accent
          ? "border-zinc-200 text-zinc-500 hover:text-[#C8102E] hover:border-[#C8102E]/30"
          : "border-transparent text-zinc-400 hover:text-zinc-700"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function FieldInput({ label, value, onChange, type = "text", required }) {
  return (
    <div>
      <label className="block text-xs font-medium text-zinc-600 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        required={required}
        className="w-full border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:border-zinc-400 bg-white"
      />
    </div>
  );
}

function FieldSelect({ label, value, onChange, options }) {
  return (
    <div>
      <label className="block text-xs font-medium text-zinc-600 mb-1">{label}</label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:border-zinc-400 bg-white"
      >
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}
